/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import {
  camelCase,
  capitalize,
  findLastIndex,
  get,
  isEmpty,
  omit,
  reduce,
} from 'lodash';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

import {
  Button,
  Dropdown,
  Grid,
  IconButton,
  Margin,
  Surface,
  TextInput,
} from '@zapier/design-system';

import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';
import { FIELD_TYPES } from 'app/developer-v3/constants';
import { getBorderColorName, quickCopy } from 'app/developer-v3/utils';

import type { DeepLinkParams } from 'app/developer-v3/types';
import type { OutputFieldSchema } from 'app/developer-v3/platformSchema/field';

const Styles = {
  container: css`
    padding: 25px;
  `,
};

const delayReverting = 3000;
const getLabelForType = capitalize;
const getDefaultItem = () => ({ key: '', label: '', type: 'string' });
const getCleanItems = items =>
  reduce(
    items,
    (res, item) => {
      if (item.hasOwnProperty('key')) {
        const isKeyEmpty = isEmpty(item.key);
        const isLabelEmpty = isEmpty(item.label);

        if (!isKeyEmpty || !isLabelEmpty) {
          // Empty string labels cause issues in schema, so strip them here
          res.push(isLabelEmpty ? omit(item, ['label']) : item);
        }
      } else {
        res.push(item);
      }

      return res;
    },
    []
  );

type Props = {
  initialItems: Array<OutputFieldSchema>;
  isDisabled: boolean;
  onUpdate: (options: Array<OutputFieldSchema>) => void;
  meta?: {
    info?: DeepLinkParams;
  };
};

function OutputFieldsEditor(props: Props) {
  const { initialItems, isDisabled, onUpdate } = props;
  const [items, setItems] = useState<Array<OutputFieldSchema>>(
    isEmpty(initialItems) ? [getDefaultItem()] : initialItems
  );
  const [indexToDelete, setIndexToDelete] = useState<number>(NaN);
  const timeoutDelete = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (indexToDelete >= 0) {
      timeoutDelete.current = setTimeout(
        () => setIndexToDelete(NaN),
        delayReverting
      );
    }

    return () => clearTimeout(timeoutDelete.current);
  }, [indexToDelete]);

  const updateItems = nextItems => {
    onUpdate(getCleanItems(nextItems));
    setItems(nextItems);
    setIndexToDelete(NaN);
  };

  const addEmptyItem = () => {
    const lastIndexWithKey = findLastIndex(items, item =>
      item.hasOwnProperty('key')
    );
    setItems([
      ...items.slice(0, lastIndexWithKey + 1),
      getDefaultItem(),
      ...items.slice(lastIndexWithKey + 1),
    ]);
  };

  const deleteItem = (index: number) => {
    const nextItems = [...items.slice(0, index), ...items.slice(index + 1)];
    updateItems(nextItems);
  };

  const changeItem = (index: number, value: string, name: string) => {
    if (index >= items.length) {
      console.error(`Item at index ${index} out of range ${items.length}.`);
      return;
    }

    const nextItems = quickCopy(items);
    nextItems[index][name] = value;
    updateItems(nextItems);
  };

  const info = get(props.meta, 'info', {});

  return (
    <Surface borderColor={getBorderColorName(info.type)}>
      <div aria-label="Output Fields Editor" css={Styles.container}>
        {items.map(
          (item, index) =>
            item.hasOwnProperty('key') && (
              <Margin key={index} margin="0px 0px 10px 0px">
                <Grid
                  alignItems="center"
                  columns="repeat(3, 1.5fr) 0.5fr"
                  gap={10}
                >
                  <TextInput
                    aria-label="Key"
                    isDisabled={isDisabled}
                    name="key"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      changeItem(index, e.target.value, 'key')
                    }
                    placeholder="Key"
                    value={item.key}
                  />
                  <TextInput
                    aria-label="Label"
                    isDisabled={isDisabled}
                    name="label"
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      changeItem(index, e.target.value, 'label')
                    }
                    placeholder="Label"
                    value={item.label || ''}
                  />
                  <Dropdown
                    aria-label="Change field type"
                    isDisabled={isDisabled}
                    items={FIELD_TYPES.map(type => ({
                      label: getLabelForType(type),
                      value: type,
                    }))}
                    menuAriaLabel="Change field type"
                    onSelect={option => {
                      changeItem(index, option.value, 'type');
                    }}
                    placeholder={getLabelForType(item.type) || 'string'}
                    size="medium"
                    value={item}
                  />
                  <IconButton
                    aria-label="Remove field"
                    disabled={isDisabled}
                    icon={indexToDelete === index ? 'actionTrashFill' : 'formX'}
                    onClick={() =>
                      (index === indexToDelete ? deleteItem : setIndexToDelete)(
                        index
                      )
                    }
                    size="medium"
                  />
                </Grid>
              </Margin>
            )
        )}
        {info && (
          <Margin margin="0px 0px 15px 0px">
            <FieldMessage
              fieldKey="output-fields"
              message={info.message}
              type={camelCase(`info ${info.type}`) as FieldType}
            />
          </Margin>
        )}
        <Button
          aria-label="Add Output Field"
          color="secondary"
          disabled={isDisabled}
          onClick={addEmptyItem}
        >
          Add Output Field
        </Button>
      </div>
    </Surface>
  );
}

export default OutputFieldsEditor;
